/**
 * Merge-field substitution for email templates.
 *
 * **Syntax is `<Field Name>`, case-sensitive** — matching Travel Booster, which is
 * where this system's users come from, and matching the chips already offered by
 * the Email Engagements form (`EmailEngagementsForm.tsx`). Templates are shared
 * with engagements, so a second syntax would mean the same author writing
 * `<Booking Num>` in one screen and something else in the next.
 *
 * **This module must stay isomorphic** — no DOM, no database, no React, no Node
 * built-ins. It runs in two places that cannot share anything heavier:
 *
 *   1. the compose dialog (browser), where a user picks a template and then edits
 *      the resolved text before sending;
 *   2. an engagement trigger (server), which fires on a schedule with no user
 *      present to preview anything.
 *
 * One implementation is what guarantees a template reads identically whether a
 * person sends it or a cron does.
 *
 * ## Ordering matters
 *
 * Substitution MUST run **before** the body is escaped into HTML. `<Booking Num>`
 * shares its delimiters with HTML tags, so escaping first would turn it into
 * `&lt;Booking Num&gt;` and nothing would resolve. The compose flow is:
 *
 *     template → applyMergeFields → (user edits) → plainTextToHtml (escapes) → send
 *
 * A field left unresolved therefore reaches the escaper and is delivered as literal
 * visible text, which is the intended failure mode: wrong, but never silent.
 */

export type MergeFieldName =
    // Present in the Email Engagements form today — do not rename.
    | 'First Customer Name'
    | 'Booking Num'
    | 'Destination'
    | 'Tour Name'
    | 'Missing Service Type'
    | 'Company Name'
    | 'Booking Left To Pay'
    | 'Booking Currency'
    // In Travel Booster but never ported; the engagement engine will need them.
    | 'Payment Link'
    | 'Travel File Link'
    // Document-oriented, needed by the compose dialog — engagements have no document.
    | 'Document Type'
    | 'Document Number'
    | 'Travel Start Date'
    | 'Travel End Date';

export interface MergeFieldDefinition {
    name: MergeFieldName;
    /** Example value for the editor's live preview. */
    sample: string;
    /** Where this field can actually resolve. Shown in the editor so an author is not
     *  surprised when a payment link comes out blank on a manually sent invoice. */
    scope: 'both' | 'engagement' | 'document';
}

export const MERGE_FIELDS: MergeFieldDefinition[] = [
    { name: 'First Customer Name', sample: 'Mr. DROR/DAVID', scope: 'both' },
    { name: 'Booking Num', sample: '2002148', scope: 'both' },
    { name: 'Company Name', sample: 'Pegasus Tours & Travel', scope: 'both' },
    { name: 'Destination', sample: 'Tokyo', scope: 'both' },
    // A destination alone is vague — several tours share one. The tour's own name
    // plus <Travel Start Date>/<Travel End Date> is what a customer recognises as
    // "the trip I booked".
    { name: 'Tour Name', sample: 'קלאסי איטליה', scope: 'both' },
    { name: 'Travel Start Date', sample: '12/09/2026', scope: 'both' },
    { name: 'Travel End Date', sample: '21/09/2026', scope: 'both' },
    { name: 'Document Type', sample: 'Receipt', scope: 'document' },
    { name: 'Document Number', sample: '10457', scope: 'document' },
    { name: 'Booking Left To Pay', sample: '1,250.00', scope: 'engagement' },
    { name: 'Booking Currency', sample: 'USD', scope: 'engagement' },
    { name: 'Missing Service Type', sample: 'Hotel', scope: 'engagement' },
    { name: 'Payment Link', sample: 'https://…/pay/abc123', scope: 'engagement' },
    { name: 'Travel File Link', sample: 'https://…/tf/2002148', scope: 'engagement' }
];

export const MERGE_FIELD_NAMES: MergeFieldName[] = MERGE_FIELDS.map((f) => f.name);

export type MergeContext = Partial<Record<MergeFieldName, string | number | null | undefined>>;

/**
 * `<Field Name>` — letters and single spaces only.
 *
 * Matched in **both** forms, because the two callers store the body differently:
 *
 *   - the subject and legacy plain-text bodies hold the raw `<Field Name>`;
 *   - the rich-text editor HTML-escapes whatever is typed or inserted, so the same
 *     field is stored as `&lt;Field Name&gt;`.
 *
 * Matching only the raw form was a silent failure: a field inserted in the editor
 * would never resolve, and the recipient would receive the literal placeholder.
 *
 * Deliberately narrow otherwise, so ordinary prose survives untouched — `a < b` and
 * `5 <10>` fail on the leading space/digit.
 */
const PLACEHOLDER_PATTERN = /(?:<|&lt;)([A-Za-z][A-Za-z]*(?: [A-Za-z]+)*)(?:>|&gt;)/g;

/**
 * HTML tags, so they can be removed before scanning for unknown fields.
 *
 * Lowercase-initial only: every HTML tag the editor emits is lowercase (`<p>`,
 * `<br>`, `<strong>`), while every merge field is Title Case. That one distinction
 * keeps real tags from being reported as misspelled fields without needing a tag
 * allowlist.
 */
const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/g;

const isKnownField = (name: string): name is MergeFieldName =>
    (MERGE_FIELD_NAMES as string[]).includes(name);

/**
 * Replace `<Field Name>` placeholders in `text`.
 *
 * Two rules that differ deliberately:
 *
 * - An **unknown or mis-cased** placeholder is left **verbatim**. Travel Booster
 *   documents this syntax as case-sensitive, so `<booking num>` is not a field; a
 *   typo then survives into the compose dialog where the sender can see it, rather
 *   than silently blanking and producing a sentence with a hole in it.
 * - A **known** field with no value resolves to an **empty string**, because the
 *   author asked for something the system genuinely has no value for here.
 */
export function applyMergeFields(text: string | undefined | null, context: MergeContext = {}): string {
    if (!text) return '';

    return String(text).replace(PLACEHOLDER_PATTERN, (match, rawName: string) => {
        if (!isKnownField(rawName)) return match;

        const value = context[rawName];
        return value === undefined || value === null ? '' : String(value);
    });
}

/** Every placeholder in `text` that is not in the catalogue. Powers the editor's warning. */
export function findUnknownMergeFields(text: string | undefined | null): string[] {
    if (!text) return [];

    // Strip real markup first, or every `<p>` and `<br>` in an editor body is
    // reported as a misspelled field.
    const withoutTags = String(text).replace(HTML_TAG_PATTERN, '');

    const unknown = new Set<string>();
    const pattern = new RegExp(PLACEHOLDER_PATTERN.source, 'g');
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(withoutTags)) !== null) {
        if (!isKnownField(match[1])) unknown.add(match[1]);
    }

    return Array.from(unknown);
}

/** Renders a template with the catalogue's sample values, for the editor preview. */
export function previewWithSamples(text: string | undefined | null): string {
    const sampleContext = MERGE_FIELDS.reduce<MergeContext>((acc, field) => {
        acc[field.name] = field.sample;
        return acc;
    }, {});
    return applyMergeFields(text, sampleContext);
}
